import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import {
  associationToHoaSnapshot,
  buildAssociationCreateHoa,
  getDirectoryNameError,
  getEmailValidationError,
  prepareAssociationPayload,
  resolveEntityId,
  uploadInstructionFiles,
} from '../HoaDirectory/HoaDirectoryHelper';
import HoaBlock from './HoaBlock';
import HoaBlockSkeleton from './HoaBlockSkeleton';

const DirectoryAssociationModal = ({
  show,
  onHide,
  mode = 'create',
  associationId = null,
  initialName = '',
  onSaved,
}) => {
  const isEdit = mode === 'edit';
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoa, setHoa] = useState(() => (isEdit ? null : buildAssociationCreateHoa(initialName)));
  const [prefetchedDirectoryFiles, setPrefetchedDirectoryFiles] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) return;

    setErrors({});
    setSaving(false);

    if (!isEdit) {
      setHoa(buildAssociationCreateHoa(initialName));
      setPrefetchedDirectoryFiles(null);
      setLoading(false);
      return;
    }

    const id = resolveEntityId(associationId);
    if (!id) {
      setHoa(null);
      setPrefetchedDirectoryFiles(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setHoa(null);
    setPrefetchedDirectoryFiles(null);

    (async () => {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(id),
        method: 'GET',
      });

      if (cancelled) return;

      if (error) {
        toast.error(error);
        setLoading(false);
        return;
      }

      setHoa(associationToHoaSnapshot(response));
      setPrefetchedDirectoryFiles({
        selfInstructionFiles: response.selfInstructionFiles || [],
        managementCompanyFiles: response.mgmtId?.orderingInstructionFiles || [],
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [show, isEdit, associationId, initialName]);

  const handleHoaChange = updatedHoa => {
    setHoa(updatedHoa);
    if (Object.keys(errors).length) setErrors({});
  };

  const validate = () => {
    const newErrors = {};
    const nameError =
      getDirectoryNameError(hoa?.name, 'Association') ||
      (!hoa?.name?.trim() ? 'Association name is required' : null);
    if (nameError) newErrors.name = nameError;

    if (!hoa.noEstoppelNeeded && hoa.managementType === 'managed') {
      const companyError =
        getDirectoryNameError(hoa.managementCompany, 'Management company') ||
        (!hoa.managementCompany?.trim() ? 'Management company is required when managed' : null);
      if (companyError) newErrors.managementCompany = companyError;

      const companyEmailError = getEmailValidationError(hoa.companyEmail);
      if (companyEmailError) newErrors.companyEmail = companyEmailError;
    }

    if (hoa.managementType === 'self') {
      const emailError = getEmailValidationError(hoa.email);
      if (emailError) newErrors.email = emailError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hoaForSave = () => ({
    ...hoa,
    orderingPlatform: hoa.orderingPlatform || '',
  });

  const uploadPendingFiles = async savedAssociation => {
    const pendingSelfFiles = hoa._pendingAssociationFiles || [];
    const pendingMgmtFiles = hoa._pendingManagementFiles || [];

    if (hoa.managementType === 'self' && pendingSelfFiles.length) {
      await uploadInstructionFiles(
        ENDPOINTS.ASSOCIATION_INSTRUCTION_FILES(savedAssociation._id),
        pendingSelfFiles
      );
    }

    if (hoa.managementType === 'managed' && pendingMgmtFiles.length) {
      const mgmtId =
        resolveEntityId(savedAssociation.mgmtId) || resolveEntityId(hoa.managementCompanyId);
      if (mgmtId) {
        await uploadInstructionFiles(
          ENDPOINTS.MANAGEMENT_COMPANY_INSTRUCTION_FILES(mgmtId),
          pendingMgmtFiles
        );
      }
    }

    return pendingSelfFiles.length > 0 || pendingMgmtFiles.length > 0;
  };

  const saveCreate = async () => {
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.ASSOCIATIONS,
      method: 'POST',
      requestBody: prepareAssociationPayload(hoaForSave()),
    });
    if (error) throw new Error(error);

    await uploadPendingFiles(response);

    const { response: fresh, error: fetchError } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(response._id),
      method: 'GET',
    });
    if (fetchError) throw new Error(fetchError);
    return fresh || response;
  };

  const saveEdit = async () => {
    const id = resolveEntityId(associationId);
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(id),
      method: 'PUT',
      requestBody: prepareAssociationPayload(hoaForSave()),
    });
    if (error) throw new Error(error);

    const uploaded = await uploadPendingFiles(response);
    const removedSelfIds = hoa._removedAssociationFileIds || [];
    const removedMgmtIds = hoa._removedManagementCompanyFileIds || [];

    let saved = response;
    if (uploaded || removedSelfIds.length || removedMgmtIds.length) {
      const { response: fresh, error: fetchError } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(response._id),
        method: 'GET',
      });
      if (!fetchError && fresh) {
        saved = fresh;
      }
    }

    return saved;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setSaving(true);
    try {
      const saved = isEdit ? await saveEdit() : await saveCreate();
      toast.success(isEdit ? 'Association updated successfully' : 'Association created successfully');
      onHide();
      onSaved?.(saved);
    } catch (err) {
      toast.error(err.message || `Failed to ${isEdit ? 'update' : 'create'} association`);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const formReady = !isEdit || (hoa && !loading);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit Association' : 'Add Association'}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        {!formReady ? (
          <HoaBlockSkeleton />
        ) : (
          <HoaBlock
            index={0}
            hoa={hoa}
            onChange={handleHoaChange}
            preferredAssociationId={resolveEntityId(associationId)}
            fieldInvalid={{
              name: !!errors.name,
              company: !!errors.managementCompany,
              email: !!errors.email,
              companyEmail: !!errors.companyEmail,
            }}
            directoryCreateMode={!isEdit}
            showProcessorFields
            noAccordion
            simpleNameField
            directoryEditMode={isEdit}
            prefetchedDirectoryFiles={isEdit ? prefetchedDirectoryFiles : null}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !formReady}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Saving...
            </>
          ) : isEdit ? (
            'Save Changes'
          ) : (
            'Create Association'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DirectoryAssociationModal;
